import { zodResolver } from '@hookform/resolvers/zod';
import {
  rolesControllerFindAll,
  userTypesControllerList,
  usersControllerAssignRole,
  usersControllerFindOne,
  usersControllerRemoveRole,
  usersControllerUpdate,
} from '@kalba/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  AtSign,
  Check,
  Mail,
  RefreshCw,
  Save,
  Search,
  Shield,
  Star,
  User,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { FormField } from '../../../components/forms/form-field';
import { FormSection } from '../../../components/forms/form-section';
import { AppLayout } from '../../../components/layout/app-layout';
import { Input } from '../../../components/ui/input';
import { PhoneInput } from '../../../components/ui/phone-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { toCanonicalUaePhone, UAE_PHONE_REGEX } from '../../../lib/phone';
import { cn } from '../../../lib/utils';
import { useAuthStore } from '../../../stores/auth.store';

export const Route = createFileRoute('/users/$userId/edit')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('user:create')) throw redirect({ to: '/dashboard' });
  },
  component: EditUserPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface UserRole {
  id: string;
  name: string;
  isSystem: boolean;
}
interface Role {
  id: string;
  name: string;
  description?: string;
  isSystem: boolean;
}
interface UserDetail {
  id: string;
  name: string;
  email: string;
  username?: string | null;
  phoneNumber?: string | null;
  status: string;
  organization?: string | null;
  userTypeId?: string | null;
  roles: UserRole[];
  permissionOverrides: unknown[];
}
interface UserTypeOption {
  id: string;
  name: string;
  status: string;
}

// ── Schema ────────────────────────────────────────────────────────────────────

const editUserSchema = z.object({
  fullName: z.string().min(1, 'Full name is required'),
  email: z.string().min(1, 'Email is required').email('Enter a valid email address'),
  username: z.string().optional(),
  phoneNumber: z
    .string()
    .optional()
    .refine((v) => !v || UAE_PHONE_REGEX.test(v), {
      message: 'Enter a valid UAE mobile number',
    }),
  status: z.enum(['ACTIVE', 'INACTIVE', 'SUSPENDED']),
  organization: z.enum(['BEEAH', 'KALBA_MUNICIPALITY', 'SHARJAH_MUNICIPALITY']).nullable(),
  userTypeId: z.string().nullable(),
});
type EditUserFormValues = z.infer<typeof editUserSchema>;

// ── Role colors ───────────────────────────────────────────────────────────────

const ROLE_COLOR_SEEDS = [
  {
    key: 'admin',
    color: 'text-indigo-700',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    dot: 'bg-indigo-500',
  },
  {
    key: 'inspector',
    color: 'text-emerald-700',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    dot: 'bg-emerald-500',
  },
  {
    key: 'finance',
    color: 'text-blue-700',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    dot: 'bg-blue-500',
  },
  {
    key: 'customer',
    color: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    dot: 'bg-amber-500',
  },
  {
    key: 'super',
    color: 'text-violet-700',
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    dot: 'bg-violet-500',
  },
];
const FALLBACK_COLORS = [
  { color: 'text-teal-700', bg: 'bg-teal-50', border: 'border-teal-200', dot: 'bg-teal-500' },
  { color: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200', dot: 'bg-rose-500' },
];
function getRoleColor(name: string, index: number) {
  const lower = name.toLowerCase();
  const seed = ROLE_COLOR_SEEDS.find((s) => lower.includes(s.key));
  return seed ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length];
}

// ── Page ──────────────────────────────────────────────────────────────────────

function EditUserPage() {
  const { userId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [initialRoleIds, setInitialRoleIds] = useState<string[]>([]);
  const [roleSearch, setRoleSearch] = useState('');
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);

  const { data: user, isLoading } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => usersControllerFindOne({ id: userId }) as unknown as Promise<UserDetail>,
  });

  const { data: roles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesControllerFindAll() as unknown as Promise<Role[]>,
  });

  const { data: userTypes = [] } = useQuery({
    queryKey: ['userTypes'],
    queryFn: () => userTypesControllerList() as Promise<UserTypeOption[]>,
  });
  const activeUserTypes = userTypes.filter((t) => t.status === 'ACTIVE');

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<EditUserFormValues>({
    resolver: zodResolver(editUserSchema),
    mode: 'onBlur',
    defaultValues: {
      fullName: '',
      email: '',
      username: '',
      phoneNumber: '',
      status: 'ACTIVE',
      organization: null,
      userTypeId: null,
    },
  });

  // Pre-fill form once user data arrives
  useEffect(() => {
    if (user) {
      reset({
        fullName: user.name,
        email: user.email,
        username: user.username ?? '',
        phoneNumber: toCanonicalUaePhone(user.phoneNumber),
        status: (user.status as EditUserFormValues['status']) ?? 'ACTIVE',
        organization: (user.organization as EditUserFormValues['organization']) ?? null,
        userTypeId: user.userTypeId ?? null,
      });
      const ids = user.roles.map((r) => r.id);
      setSelectedRoleIds(ids);
      setInitialRoleIds(ids);
    }
  }, [user, reset]);

  const fullName = watch('fullName');
  const email = watch('email');

  const updateMutation = useMutation({
    mutationFn: async (data: EditUserFormValues) => {
      // Update name/email/username/phoneNumber/status
      await usersControllerUpdate({
        id: userId,
        requestBody: {
          name: data.fullName,
          email: data.email,
          username: data.username || undefined,
          phoneNumber: data.phoneNumber || undefined,
          status: data.status,
          organization: data.organization,
          userTypeId: data.userTypeId,
        },
      });

      // Sync roles: add new ones, remove removed ones
      const toAdd = selectedRoleIds.filter((id) => !initialRoleIds.includes(id));
      const toRemove = initialRoleIds.filter((id) => !selectedRoleIds.includes(id));
      await Promise.all([
        ...toAdd.map((roleId) =>
          usersControllerAssignRole({ id: userId, requestBody: { roleId } }),
        ),
        ...toRemove.map((roleId) => usersControllerRemoveRole({ id: userId, roleId })),
      ]);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user', userId] });
      toast.success('User updated successfully');
      navigate({ to: '/users/$userId', params: { userId } });
    },
    onError: (err: unknown) => {
      const body = (err as { body?: { message?: string | string[] } })?.body;
      const raw = body?.message;
      const lines = Array.isArray(raw) ? raw : [raw ?? 'Failed to update user.'];
      toast.error('Failed to update user', { description: lines.join(' · '), duration: 5000 });
    },
  });

  const selectedRoles = roles.filter((r) => selectedRoleIds.includes(r.id));
  const availableRoles = roles.filter(
    (r) =>
      !selectedRoleIds.includes(r.id) && r.name.toLowerCase().includes(roleSearch.toLowerCase()),
  );

  const isFormValid = !!(fullName && email && selectedRoleIds.length > 0);

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-24">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      </AppLayout>
    );
  }

  if (!user) {
    return (
      <AppLayout>
        <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-sm font-medium text-red-600">User not found.</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout noPadding>
      <div className="flex flex-col min-h-[calc(100vh-4rem)]">
        {/* ── Header ── */}
        <div className="bg-white border-b border-gray-200 px-8 py-6">
          <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-4">
            <span>Settings</span>
            <span>/</span>
            <button
              type="button"
              onClick={() => navigate({ to: '/users' })}
              className="hover:text-foreground transition-colors"
            >
              Users
            </button>
            <span>/</span>
            <button
              type="button"
              onClick={() => navigate({ to: '/users/$userId', params: { userId } })}
              className="hover:text-foreground transition-colors"
            >
              {user.name}
            </button>
            <span>/</span>
            <span className="text-foreground font-medium">Edit</span>
          </nav>

          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-500/30 shrink-0">
                <User className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight">Edit User</h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Update profile and role assignments
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => navigate({ to: '/users/$userId', params: { userId } })}
                className="px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium text-gray-500 flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" /> Cancel
              </button>
              <button
                type="button"
                disabled={!isFormValid || updateMutation.isPending}
                onClick={handleSubmit((data) => updateMutation.mutate(data)) as () => void}
                className={cn(
                  'px-5 py-2 rounded-xl text-sm font-semibold transition-all flex items-center gap-2',
                  isFormValid && !updateMutation.isPending
                    ? 'bg-brand-500 text-white hover:bg-brand-600 shadow-sm shadow-brand-200'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed',
                )}
              >
                {updateMutation.isPending ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" /> Saving…
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" /> Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ── Two-column body ── */}
        <div className="flex flex-1 min-h-0">
          {/* Left: form */}
          <div className="flex-1 px-8 py-7 space-y-5 overflow-y-auto min-w-0">
            {/* Section 1: Basic Information */}
            <FormSection
              icon={<User className="w-4 h-4" />}
              iconBg="bg-secondary/10 text-secondary"
              title="Basic Information"
              subtitle="Identity and contact details"
            >
              <div className="grid grid-cols-2 gap-5">
                {/* Full Name */}
                <FormField
                  className="col-span-2"
                  label="Full Name"
                  required
                  error={errors.fullName?.message}
                >
                  <Controller
                    control={control}
                    name="fullName"
                    render={({ field }) => (
                      <div className="relative">
                        <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <Input
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          placeholder="First and last name"
                          hasError={!!errors.fullName}
                          className="pl-10"
                        />
                      </div>
                    )}
                  />
                </FormField>

                {/* Email */}
                <FormField label="Email Address" required error={errors.email?.message}>
                  <Controller
                    control={control}
                    name="email"
                    render={({ field }) => (
                      <div className="relative">
                        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <Input
                          type="email"
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          placeholder="user@company.com"
                          hasError={!!errors.email}
                          className="pl-10"
                        />
                      </div>
                    )}
                  />
                </FormField>

                {/* Username */}
                <FormField label="Username" error={errors.username?.message}>
                  <Controller
                    control={control}
                    name="username"
                    render={({ field }) => (
                      <div className="relative">
                        <AtSign className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <Input
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          placeholder="janesmith"
                          hasError={!!errors.username}
                          className="pl-10"
                        />
                      </div>
                    )}
                  />
                </FormField>

                {/* Phone Number */}
                <FormField label="Phone Number" error={errors.phoneNumber?.message}>
                  <Controller
                    control={control}
                    name="phoneNumber"
                    render={({ field }) => (
                      <PhoneInput
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        placeholder="502305316"
                        hasError={!!errors.phoneNumber}
                      />
                    )}
                  />
                </FormField>

                {/* Status */}
                <FormField label="Account Status">
                  <Controller
                    control={control}
                    name="status"
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ACTIVE">Active</SelectItem>
                          <SelectItem value="INACTIVE">Inactive</SelectItem>
                          <SelectItem value="SUSPENDED">Suspended</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </FormField>

                {/* Organization */}
                <FormField label="Organization">
                  <Controller
                    control={control}
                    name="organization"
                    render={({ field }) => (
                      <Select
                        value={field.value ?? 'NONE'}
                        onValueChange={(v) => field.onChange(v === 'NONE' ? null : v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select organization" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="NONE">None</SelectItem>
                          <SelectItem value="BEEAH">Beeah</SelectItem>
                          <SelectItem value="KALBA_MUNICIPALITY">Kalba Municipality</SelectItem>
                          <SelectItem value="SHARJAH_MUNICIPALITY">Sharjah Municipality</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </FormField>

                {/* User Type */}
                <FormField label="User Type">
                  <Controller
                    control={control}
                    name="userTypeId"
                    render={({ field }) => (
                      <Select
                        value={field.value ?? 'NONE'}
                        onValueChange={(v) => field.onChange(v === 'NONE' ? null : v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select user type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="NONE">None</SelectItem>
                          {activeUserTypes.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </FormField>
              </div>
            </FormSection>

            {/* Section 2: Roles */}
            <FormSection
              className="overflow-visible"
              icon={<Star className="w-4 h-4" />}
              iconBg="bg-warning/10 text-warning"
              title="Roles & Permissions *"
              subtitle="Assign one or more roles to control access"
              badge={
                selectedRoleIds.length > 0 ? (
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
                    {selectedRoleIds.length} assigned
                  </span>
                ) : undefined
              }
            >
              <div className="space-y-4">
                {/* Selected role chips */}
                {selectedRoles.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200 min-h-[48px]">
                    {selectedRoles.map((r, i) => {
                      const rc = getRoleColor(r.name, i);
                      return (
                        <span
                          key={r.id}
                          className={cn(
                            'inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 rounded-full text-xs font-semibold border',
                            rc.bg,
                            rc.color,
                            rc.border,
                          )}
                        >
                          <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', rc.dot)} />
                          {r.name}
                          <button
                            type="button"
                            onClick={() => setSelectedRoleIds((p) => p.filter((id) => id !== r.id))}
                            className="w-4 h-4 rounded-full hover:bg-black/10 flex items-center justify-center transition-colors ml-0.5"
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Role search */}
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                    <input
                      value={roleSearch}
                      onChange={(e) => {
                        setRoleSearch(e.target.value);
                        setRoleDropdownOpen(true);
                      }}
                      onFocus={() => setRoleDropdownOpen(true)}
                      placeholder={
                        selectedRoleIds.length === 0
                          ? 'Search and assign roles…'
                          : 'Add another role…'
                      }
                      className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400 transition-all placeholder:text-gray-400"
                    />
                  </div>

                  {roleDropdownOpen && availableRoles.length > 0 && (
                    <div className="absolute z-20 top-full mt-1.5 w-full bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
                      <div className="p-1.5">
                        {availableRoles.map((r, i) => {
                          const rc = getRoleColor(r.name, i);
                          return (
                            <button
                              key={r.id}
                              type="button"
                              onClick={() => {
                                setSelectedRoleIds((p) => [...p, r.id]);
                                setRoleSearch('');
                                setRoleDropdownOpen(false);
                              }}
                              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors text-left group"
                            >
                              <span className={cn('w-2 h-2 rounded-full shrink-0', rc.dot)} />
                              <span className="text-sm font-medium flex-1">{r.name}</span>
                              <span
                                className={cn(
                                  'text-xs px-2 py-0.5 rounded-full border opacity-0 group-hover:opacity-100 transition-opacity',
                                  rc.bg,
                                  rc.color,
                                  rc.border,
                                )}
                              >
                                Add
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {roleDropdownOpen && (
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setRoleDropdownOpen(false)}
                    />
                  )}
                </div>
              </div>
            </FormSection>

            {/* Security section */}
            <FormSection
              icon={<Shield className="w-4 h-4" />}
              iconBg="bg-primary/10 text-primary"
              title="Security"
              subtitle="Password and authentication"
            >
              <p className="text-sm text-gray-500">
                To reset this user's password, use the{' '}
                <span className="font-semibold text-gray-900">Reset Password</span> button on the
                user profile page. A reset link will be sent to{' '}
                <span className="font-semibold text-gray-900">{email || user.email}</span>.
              </p>
            </FormSection>
          </div>

          {/* Right: preview panel */}
          <div className="w-72 shrink-0 px-6 py-7">
            <div className="sticky top-20 space-y-4">
              <div className="bg-white rounded-2xl border border-gray-200 shadow-theme-sm overflow-hidden">
                <div className="p-5 border-b border-gray-100">
                  <p className="font-semibold text-sm">User Preview</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Live summary as you edit</p>
                </div>
                <div className="p-5 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-md shrink-0">
                      {fullName ? (
                        <span className="text-white font-bold text-sm">
                          {fullName
                            .split(' ')
                            .filter(Boolean)
                            .map((w) => w[0])
                            .slice(0, 2)
                            .join('')
                            .toUpperCase()}
                        </span>
                      ) : (
                        <User className="w-5 h-5 text-white/70" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{fullName || user.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {email || user.email}
                      </p>
                    </div>
                  </div>

                  {selectedRoles.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                        Roles
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedRoles.map((r, i) => {
                          const rc = getRoleColor(r.name, i);
                          return (
                            <span
                              key={r.id}
                              className={cn(
                                'px-2 py-0.5 rounded-full text-xs font-semibold border',
                                rc.bg,
                                rc.color,
                                rc.border,
                              )}
                            >
                              {r.name}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="space-y-1.5 pt-2 border-t border-gray-100">
                    {[
                      { label: 'Full name', done: !!fullName },
                      { label: 'Role assigned', done: selectedRoleIds.length > 0 },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-2.5 py-1">
                        <div
                          className={cn(
                            'w-4 h-4 rounded-full flex items-center justify-center shrink-0 transition-all',
                            item.done ? 'bg-success' : 'bg-border',
                          )}
                        >
                          {item.done && <Check className="w-2.5 h-2.5 text-white" />}
                        </div>
                        <span
                          className={cn(
                            'text-xs',
                            item.done ? 'text-foreground font-medium' : 'text-muted-foreground',
                          )}
                        >
                          {item.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
